import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { userSchema } from '../../schemas';
import {
  GET_RELATED_USER_IDS_FULFILLED,
  GET_RELATED_USER_IDS_PENDING,
  GET_RELATED_USER_IDS_REJECTED,
  GET_USER_ARTICLES_FULFILLED,
  GET_USER_ARTICLES_PENDING,
  GET_USER_ARTICLES_REJECTED,
  GET_USER_DAILY_ACTIVITIES_DETAILS_FULFILLED,
  GET_USER_DAILY_ACTIVITIES_DETAILS_PENDING,
  GET_USER_DAILY_ACTIVITIES_DETAILS_REJECTED,
  GET_USER_FANS_FULFILLED,
  GET_USER_FANS_PENDING,
  GET_USER_FANS_REJECTED,
  GET_USER_INFO_FULFILLED,
  GET_USER_INFO_PENDING,
  GET_USER_INFO_REJECTED,
  GET_USER_INTERESTS_FULFILLED,
  GET_USER_INTERESTS_PENDING,
  GET_USER_INTERESTS_REJECTED,
  GET_USER_LOCATIONS_FULFILLED,
  GET_USER_LOCATIONS_PENDING,
  GET_USER_LOCATIONS_REJECTED,
  GET_USER_PUSHES_STATS_FULFILLED,
  GET_USER_PUSHES_STATS_PENDING,
  GET_USER_PUSHES_STATS_REJECTED,
  GET_USER_RECENT_ACTIVITIES_FULFILLED,
  GET_USER_RECENT_ACTIVITIES_PENDING,
  GET_USER_RECENT_ACTIVITIES_REJECTED,
} from '../../types';

// State interfaces
interface InfoState {
  user: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface LocationsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface InterestsState {
  data: {
    pushesBoardsBreakdown: any[];
    pushesCategoriesBreakdown: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface PushesStatsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface RecentActivitiesState {
  data: {
    date: any[];
    times: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface DailyActivitiesDetailsState {
  data: {
    articles: any[];
    pushes: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface ArticlesState {
  user: string | null;
  data: any[];
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface RelatedIDsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface FansState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface UserState {
  articles: ArticlesState;
  dailyActivitiesDetails: DailyActivitiesDetailsState;
  fans: FansState;
  info: InfoState;
  interests: InterestsState;
  locations: LocationsState;
  pushesStats: PushesStatsState;
  recentActivities: RecentActivitiesState;
  relatedIDs: RelatedIDsState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_USER_INFO_PENDING | typeof GET_USER_LOCATIONS_PENDING |
        typeof GET_USER_INTERESTS_PENDING | typeof GET_USER_PUSHES_STATS_PENDING |
        typeof GET_USER_RECENT_ACTIVITIES_PENDING | typeof GET_USER_DAILY_ACTIVITIES_DETAILS_PENDING |
        typeof GET_USER_ARTICLES_PENDING | typeof GET_RELATED_USER_IDS_PENDING |
        typeof GET_USER_FANS_PENDING;
}

interface RejectedAction {
  type: typeof GET_USER_INFO_REJECTED | typeof GET_USER_LOCATIONS_REJECTED |
        typeof GET_USER_INTERESTS_REJECTED | typeof GET_USER_PUSHES_STATS_REJECTED |
        typeof GET_USER_RECENT_ACTIVITIES_REJECTED | typeof GET_USER_DAILY_ACTIVITIES_DETAILS_REJECTED |
        typeof GET_USER_ARTICLES_REJECTED | typeof GET_RELATED_USER_IDS_REJECTED |
        typeof GET_USER_FANS_REJECTED;
  error: any;
}

interface GetUserInfoFulfilledAction {
  type: typeof GET_USER_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetUserDataFulfilledAction {
  type: typeof GET_USER_LOCATIONS_FULFILLED | typeof GET_USER_PUSHES_STATS_FULFILLED |
        typeof GET_USER_RECENT_ACTIVITIES_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetUserInterestsFulfilledAction {
  type: typeof GET_USER_INTERESTS_FULFILLED;
  payload: [
    { data: any[] },
    { data: any[] }
  ];
}

interface GetUserDailyActivitiesDetailsFulfilledAction {
  type: typeof GET_USER_DAILY_ACTIVITIES_DETAILS_FULFILLED;
  payload: {
    data: {
      articles: any[];
      pushes: any[];
    };
  };
}

interface GetUserArticlesFulfilledAction {
  type: typeof GET_USER_ARTICLES_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    user: string;
    offset: number;
    limit: number;
  };
}

interface GetRelatedUserIdsFulfilledAction {
  type: typeof GET_RELATED_USER_IDS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetUserFansFulfilledAction {
  type: typeof GET_USER_FANS_FULFILLED;
  payload: {
    data: any[];
  };
}

type InfoAction = PendingAction | GetUserInfoFulfilledAction | RejectedAction;
type LocationsAction = PendingAction | GetUserDataFulfilledAction | RejectedAction;
type InterestsAction = PendingAction | GetUserInterestsFulfilledAction | RejectedAction;
type PushesStatsAction = PendingAction | GetUserDataFulfilledAction | RejectedAction;
type RecentActivitiesAction = PendingAction | GetUserDataFulfilledAction | RejectedAction;
type DailyActivitiesDetailsAction = PendingAction | GetUserDailyActivitiesDetailsFulfilledAction | RejectedAction;
type ArticlesAction = PendingAction | GetUserArticlesFulfilledAction | RejectedAction;
type RelatedIDsAction = PendingAction | GetRelatedUserIdsFulfilledAction | RejectedAction;
type FansAction = PendingAction | GetUserFansFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  INFO: {
    user: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  LOCATIONS: {
    data: [],
    isFetching: false,
    error: null,
  },
  INTERESTS: {
    data: {
      pushesBoardsBreakdown: [],
      pushesCategoriesBreakdown: [],
    },
    isFetching: false,
    error: null,
  },
  PUSHES_STATS: {
    data: [],
    isFetching: false,
    error: null,
  },
  RECENT_ACTIVITIES: {
    data: {
      date: [],
      times: [],
    },
    isFetching: false,
    error: null,
  },
  DAILY_ACTIVITIES_DETAILS: {
    data: {
      articles: [],
      pushes: [],
    },
    isFetching: false,
    error: null,
  },
  ARTICLES: {
    user: null,
    data: [],
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  RELATED_IDS: {
    data: [],
    isFetching: false,
    error: null,
  },
  FANS: {
    data: [],
    isFetching: false,
    error: null,
  },
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_USER_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_USER_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;
      if (!data) {
        return INITIAL_STATE.INFO;
      }
      const {
        entities,
        result,
      } = normalize(data, userSchema);
      return {
        entities: merge(state.entities, entities),
        user: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const locations = (state: LocationsState = INITIAL_STATE.LOCATIONS, action: LocationsAction): LocationsState => {
  switch (action.type) {
    case GET_USER_LOCATIONS_PENDING:
      return {
        ...INITIAL_STATE.LOCATIONS,
        isFetching: true,
      };
    case GET_USER_LOCATIONS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_LOCATIONS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const interests = (state: InterestsState = INITIAL_STATE.INTERESTS, action: InterestsAction): InterestsState => {
  switch (action.type) {
    case GET_USER_INTERESTS_PENDING:
      return {
        ...INITIAL_STATE.INTERESTS,
        isFetching: true,
      };
    case GET_USER_INTERESTS_FULFILLED: {
      const [
        pushesBoardsBreakdown,
        pushesCategoriesBreakdown,
      ] = action.payload;
      return {
        data: {
          pushesBoardsBreakdown: pushesBoardsBreakdown.data,
          pushesCategoriesBreakdown: pushesCategoriesBreakdown.data,
        },
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_INTERESTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const pushesStats = (state: PushesStatsState = INITIAL_STATE.PUSHES_STATS, action: PushesStatsAction): PushesStatsState => {
  switch (action.type) {
    case GET_USER_PUSHES_STATS_PENDING:
      return {
        ...INITIAL_STATE.PUSHES_STATS,
        isFetching: true,
      };
    case GET_USER_PUSHES_STATS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_PUSHES_STATS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const recentActivities = (state: RecentActivitiesState = INITIAL_STATE.RECENT_ACTIVITIES, action: RecentActivitiesAction): RecentActivitiesState => {
  switch (action.type) {
    case GET_USER_RECENT_ACTIVITIES_PENDING:
      return {
        ...INITIAL_STATE.RECENT_ACTIVITIES,
        isFetching: true,
      };
    case GET_USER_RECENT_ACTIVITIES_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_RECENT_ACTIVITIES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const dailyActivitiesDetails = (state: DailyActivitiesDetailsState = INITIAL_STATE.DAILY_ACTIVITIES_DETAILS, action: DailyActivitiesDetailsAction): DailyActivitiesDetailsState => {
  switch (action.type) {
    case GET_USER_DAILY_ACTIVITIES_DETAILS_PENDING:
      return {
        ...INITIAL_STATE.DAILY_ACTIVITIES_DETAILS,
        isFetching: true,
      };
    case GET_USER_DAILY_ACTIVITIES_DETAILS_FULFILLED:
      return {
        data: action.payload.data,
        isFetching: false,
        error: null,
      };
    case GET_USER_DAILY_ACTIVITIES_DETAILS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const articles = (state: ArticlesState = INITIAL_STATE.ARTICLES, action: ArticlesAction): ArticlesState => {
  switch (action.type) {
    case GET_USER_ARTICLES_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_USER_ARTICLES_FULFILLED: {
      const {
        user,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        user,
        data,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_ARTICLES_REJECTED:
      return {
        ...INITIAL_STATE.ARTICLES,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const relatedIDs = (state: RelatedIDsState = INITIAL_STATE.RELATED_IDS, action: RelatedIDsAction): RelatedIDsState => {
  switch (action.type) {
    case GET_RELATED_USER_IDS_PENDING:
      return {
        ...INITIAL_STATE.RELATED_IDS,
        isFetching: true,
      };
    case GET_RELATED_USER_IDS_FULFILLED:
      return {
        data: action.payload.data,
        isFetching: false,
        error: null,
      };
    case GET_RELATED_USER_IDS_REJECTED:
      return {
        ...INITIAL_STATE.RELATED_IDS,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const fans = (state: FansState = INITIAL_STATE.FANS, action: FansAction): FansState => {
  switch (action.type) {
    case GET_USER_FANS_PENDING:
      return {
        ...INITIAL_STATE.FANS,
        isFetching: true,
      };
    case GET_USER_FANS_FULFILLED:
      return {
        data: action.payload.data,
        isFetching: false,
        error: null,
      };
    case GET_USER_FANS_REJECTED:
      return {
        ...INITIAL_STATE.FANS,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<UserState>({
  articles,
  dailyActivitiesDetails,
  fans,
  info,
  interests,
  locations,
  pushesStats,
  recentActivities,
  relatedIDs,
});